import { useState } from 'react';
import {
  collection,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  runTransaction,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getDb, getFunctions } from '../../firebase/utils';
import { ApprovalRequestInput, ApprovalActionInput } from '../types/approvalRequest';
import { LeaveBalance } from '../types/leaveBalance';
import { useCurrentUser } from './useCurrentUser';
import { getApproverIdFromOrgUnit } from '../utils/approvalHelper';
import { resolveApproverForOrgUnit, NoApproverError, NO_APPROVER_ERROR_CODE } from '../utils/resolveApprover'; // V169.1: SSoT
import { calculateCompOffAccrual } from '../utils/overtimeToCompOff';

export function useApprovalMutations() {
  const { user } = useCurrentUser();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * 승인 요청 생성 (V169.1: Cloud Function 기반 - 서버 권위 강제)
   *
   * **V169.1 변경:**
   * - 프론트엔드 Firestore 직접 쓰기 → Cloud Function 호출
   * - 서버에서 SSoT 규칙으로 승인자 결정 (서버 권위)
   * - 클라이언트가 보낸 approverId는 무시됨
   *
   * **승인자 결정 규칙 (서버에서 수행):**
   * 1. approverEmployeeId 우선
   * 2. role_manager 중 사번 최솟값
   * 3. role_exec 중 사번 최솟값
   * 4. 없으면 에러 (no-approver)
   */
  const createApprovalRequest = async (input: ApprovalRequestInput) => {
    if (!user?.profile?.id) {
      throw new Error('로그인이 필요합니다.');
    }

    try {
      setIsProcessing(true);
      setError(null);

      // V169.1: Cloud Function 호출 (서버 권위)
      const functions = getFunctions();
      const createApprovalRequestFn = httpsCallable<
        {
          type: string;
          targetDocumentId?: string;
          targetCollection?: string;
          requestData?: any;
        },
        {
          success: boolean;
          approvalRequestId: string;
          approverId: string;
          approverName: string;
          approverResolvedBy: string;
        }
      >(functions, 'createApprovalRequest');

      const result = await createApprovalRequestFn({
        type: input.type,
        targetDocumentId: input.targetDocumentId,
        targetCollection: input.targetCollection,
        requestData: input.requestData,
      });

      return result.data.approvalRequestId;
    } catch (err: any) {
      console.error('승인 요청 생성 실패:', err);

      // V169.1: no-approver 에러 처리
      if (err.code === 'functions/failed-precondition') {
        const details = err.details;
        if (details?.code === NO_APPROVER_ERROR_CODE) {
          // NoApproverError 형태로 변환하여 throw
          const noApproverError = new NoApproverError(
            details.orgUnitId || '',
            details.orgUnitName
          );
          setError(noApproverError);
          throw noApproverError;
        }
      }

      // Firebase Functions 에러 처리
      if (err.code) {
        const errorMessage = err.message || '승인 요청 생성 중 오류가 발생했습니다.';
        setError(new Error(errorMessage));
        throw new Error(errorMessage);
      }

      setError(err as Error);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * 승인 처리 (Cloud Functions 기반)
   *
   * **변경 사항 (Phase 3):**
   * - Firestore 직접 쓰기 → Cloud Functions 호출로 변경
   * - 서버 측에서 Self-Approval 방지 및 권한 검증 수행
   * - 도메인별 상태 동기화 및 비즈니스 로직은 서버에서 처리
   *
   * **V159 변경:**
   * - 프론트엔드 Self-Approval 사전검증 제거
   * - 백엔드(Cloud Functions)에서만 Self-Approval 검증 수행
   * - 이유: 프론트 검증 로직이 requesterId와 currentUserId를 잘못 비교하여
   *   타인 승인까지 차단하는 버그 발생 (V159_APPROVAL_SELFAPPROVAL_BUG_ANALYSIS_REPORT.md 참조)
   */
  const approveRequest = async (input: ApprovalActionInput) => {
    if (!user?.profile?.id) {
      throw new Error('로그인이 필요합니다.');
    }

    try {
      setIsProcessing(true);
      setError(null);

      // V159: 프론트엔드 Self-Approval 사전검증 제거
      // - 백엔드(Cloud Functions)에서 validateSelfApprovalWithOrgCheck로 정확히 검증
      // - 1인 조직 예외도 백엔드에서 처리

      // Cloud Functions 호출 (서버 측 처리)
      const functions = getFunctions();
      const isApprove = input.action === 'approve';

      if (isApprove) {
        // 승인
        const approveApprovalRequest = httpsCallable(functions, 'approveApprovalRequest');
        await approveApprovalRequest({
          approvalRequestId: input.approvalRequestId,
          comment: input.comment,
        });
      } else {
        // 반려
        const rejectApprovalRequest = httpsCallable(functions, 'rejectApprovalRequest');
        await rejectApprovalRequest({
          approvalRequestId: input.approvalRequestId,
          comment: input.comment,
        });
      }
    } catch (err: any) {
      console.error('승인 처리 실패:', err);

      // Firebase Functions 에러 처리
      if (err.code) {
        // HttpsError의 message 전달
        const errorMessage = err.message || '승인 처리 중 오류가 발생했습니다.';
        setError(new Error(errorMessage));
        throw new Error(errorMessage);
      }

      setError(err as Error);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * ============================================
   * 삭제된 함수: processApprovedRequest
   * ============================================
   *
   * **Phase 3 변경:**
   * - 승인 완료 후 처리 로직을 Cloud Functions로 이동
   * - 위치: functions/src/modules/approval/handler.ts
   * - 함수: updateTargetDocumentByType()
   *
   * **이유:**
   * 1. Firestore Rules에서 allow write: if false로 차단 중
   * 2. 서버 측에서 비즈니스 로직 처리가 보안상 안전
   * 3. 대체휴무 적립, 근무일정 반영 등 복잡한 로직은 서버에서 처리
   */

  /**
   * ============================================
   * 삭제된 함수: accrueCompensatoryLeave
   * ============================================
   *
   * **Phase 3 변경:**
   * - 대체휴무 적립 로직을 Cloud Functions로 이동 예정 (TODO Phase 4)
   * - 현재는 승인 시 approvedMinutes만 계산
   * - 향후 Cloud Functions에서 잔액 업데이트 및 이력 생성
   */

  /**
   * LEGACY CODE - 아래 함수들은 삭제 예정 (Phase 3 완료 후)
   */
  const processApprovedRequest_DEPRECATED = async (requestData: any, targetData: any) => {
    const { type, targetCollection, targetDocumentId, requesterId, requesterName } = requestData;

    try {
      // 연장근로 요청: 승인된 시간 계산 + 대체휴무 적립 (Phase 6-2)
      if (type === 'overtime' || type === 'weekendWork' || type === 'holidayWork') {
        const { startTime, endTime } = targetData;
        const [startHour, startMinute] = startTime.split(':').map(Number);
        const [endHour, endMinute] = endTime.split(':').map(Number);
        const approvedMinutes = (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
        const firestore = getDb();

        await updateDoc(doc(firestore, targetCollection, targetDocumentId), {
          approvedMinutes,
        });

        // TODO: 대체휴무 적립 로직 구현 필요 (Phase 6-2)
        // 현재 accrueCompensatoryLeave 함수가 완전히 구현되지 않아 주석 처리됨
        // Cloud Function으로 구현하거나 overtimeConfig를 Firestore에서 가져와야 함
        /*
        await accrueCompensatoryLeave({
          employeeId: requesterId,
          employeeName: requesterName,
          overtimeRequestId: targetDocumentId,
          overtimeType: type === 'overtime' ? 'overtime' : type === 'weekendWork' ? 'weekend' : 'holiday',
          totalOvertimeMinutes: approvedMinutes,
          approverId: user?.profile?.id || 'SYSTEM',
          approverName: user?.profile?.displayName || '시스템',
        });
        */
      }

      // 출퇴근 수정 요청: 출퇴근 기록 업데이트
      if (type === 'attendanceManual') {
        const firestore = getDb();
        const { attendanceId, requestedCheckInTime, requestedCheckOutTime } = targetData;

        if (attendanceId) {
          // 기존 출퇴근 기록 업데이트
          const updates: any = {
            updatedAt: serverTimestamp(),
          };

          if (requestedCheckInTime) {
            updates.checkInTime = requestedCheckInTime;
          }
          if (requestedCheckOutTime) {
            updates.checkOutTime = requestedCheckOutTime;
          }

          // 근무시간 재계산
          if (requestedCheckInTime && requestedCheckOutTime) {
            const workMinutes = Math.floor(
              (requestedCheckOutTime.toMillis() - requestedCheckInTime.toMillis()) / (1000 * 60)
            );
            updates.actualWorkMinutes = Math.max(0, workMinutes - 60); // 점심시간 1시간 제외
          }

          await updateDoc(doc(firestore, 'attendances', attendanceId), updates);
        } else {
          // 새 출퇴근 기록 생성
          await addDoc(collection(firestore, 'attendances'), {
            employeeId: targetData.employeeId,
            employeeName: targetData.employeeName,
            date: targetData.date,
            checkInTime: requestedCheckInTime,
            checkOutTime: requestedCheckOutTime,
            isLate: false,
            isAutoGenerated: false,
            actualWorkMinutes: requestedCheckInTime && requestedCheckOutTime
              ? Math.max(0, Math.floor((requestedCheckOutTime.toMillis() - requestedCheckInTime.toMillis()) / (1000 * 60)) - 60)
              : 0,
            overtimeMinutes: 0,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        }
      }

      // 근로일정 변경 요청: 근로일정 업데이트
      if (type === 'workScheduleChange') {
        const firestore = getDb();
        const { workScheduleId, date, changes } = targetData;

        const workScheduleRef = doc(firestore, 'workSchedules', workScheduleId);
        const workScheduleSnap = await getDoc(workScheduleRef);

        if (workScheduleSnap.exists()) {
          const workSchedule = workScheduleSnap.data();
          const updatedSchedules = {
            ...workSchedule.schedules,
            [date]: {
              ...workSchedule.schedules[date],
              ...changes,
            },
          };

          await updateDoc(workScheduleRef, {
            schedules: updatedSchedules,
            updatedAt: serverTimestamp(),
          });
        }
      }
    } catch (err) {
      console.error('승인 완료 후 처리 실패:', err);
      // 에러가 발생해도 승인은 완료된 상태로 유지
    }
  };

  /**
   * 대체휴무 적립 처리 (Phase 6-2)
   *
   * 연장근로 승인 시 직위별 기준 시간을 초과한 시간에 대해
   * 대체휴무를 자동으로 적립합니다.
   */
  const accrueCompensatoryLeave_DEPRECATED = async (input: {
    employeeId: string;
    employeeName: string;
    overtimeRequestId: string;
    overtimeType: 'overtime' | 'weekend' | 'holiday';
    totalOvertimeMinutes: number;
    approverId: string;
    approverName: string;
  }) => {
    try {
      const firestore = getDb();

      // 1. 직원 정보 조회 (직위 정보 필요)
      const employeeRef = doc(firestore, 'users', input.employeeId);
      const employeeSnap = await getDoc(employeeRef);

      if (!employeeSnap.exists()) {
        console.warn('직원 정보를 찾을 수 없어 대체휴무 적립을 건너뜁니다.');
        return;
      }

      const employeeData = employeeSnap.data();
      const positionId = employeeData.positionId;

      // 2. 대체휴무 적립 계산
      const accrualResult = calculateCompOffAccrual({
        employeeId: input.employeeId,
        employeeName: input.employeeName,
        positionId,
        totalOvertimeMinutes: input.totalOvertimeMinutes,
        overtimeType: input.overtimeType,
        overtimeRequestId: input.overtimeRequestId,
        approverId: input.approverId,
        approverName: input.approverName,
      }, {
        // TODO: overtimeConfig를 Firestore에서 가져와야 함 (systemConfigurations/overtimeConfig)
        positionThresholdHours: {},
        defaultThresholdHours: 20,
        compOffWeight: 1.5,
        compOffHoursPerDay: 8,
      });

      // 3. 적립이 없는 경우 (기준 시간 미달)
      if (!accrualResult.shouldAccrue) {
        return;
      }

      // 4. 트랜잭션으로 잔액 업데이트 + 이력 생성
      await runTransaction(firestore, async (transaction) => {
        // 4-1. 잔액 조회
        const balanceRef = doc(firestore, 'leaveBalances', input.employeeId);
        const balanceDoc = await transaction.get(balanceRef);

        let balance: LeaveBalance;
        if (!balanceDoc.exists()) {
          // 잔액이 없으면 초기화
          console.warn('휴가 잔액이 없어 초기화합니다.');
          balance = {
            id: input.employeeId,
            employeeId: input.employeeId,
            employeeName: input.employeeName,
            annual: {
              granted: 0,
              used: 0,
              remaining: 0,
              carryOver: 0,
            },
            compensatory: {
              granted: 0,
              used: 0,
              remaining: 0,
            },
            personalHealing: {
              q1Used: false,
              q2Used: false,
              q3Used: false,
              q4Used: false,
            },
            updatedAt: serverTimestamp(),
          } as LeaveBalance;
        } else {
          balance = { id: balanceDoc.id, ...balanceDoc.data() } as LeaveBalance;
        }

        // 4-2. 잔액 계산
        const balanceBefore = balance.compensatory?.remaining || 0;
        const newGranted = (balance.compensatory?.granted || 0) + accrualResult.accrualDays;
        const newRemaining = balanceBefore + accrualResult.accrualDays;

        // 4-3. 잔액 업데이트
        transaction.set(balanceRef, {
          ...balance,
          compensatory: {
            granted: newGranted,
            used: balance.compensatory?.used || 0,
            remaining: newRemaining,
          },
          updatedAt: serverTimestamp(),
          updatedBy: input.approverId,
        }, { merge: true });

        // 4-4. 이력 생성
        const historyRef = doc(collection(firestore, 'leaveHistories'));
        transaction.set(historyRef, {
          employeeId: input.employeeId,
          employeeName: input.employeeName,
          historyType: 'granted',
          leaveTypeCode: 'compensatory',
          leaveTypeName: '대체휴무',
          changeDays: accrualResult.accrualDays,
          balanceBefore,
          balanceAfter: newRemaining,
          source: input.overtimeType,
          referenceId: input.overtimeRequestId,
          reason: `${
            input.overtimeType === 'overtime'
              ? '연장근무'
              : input.overtimeType === 'weekend'
              ? '휴일근무'
              : '휴일근무'
          } 승인으로 인한 대체휴무 적립`,
          metadata: {
            approverId: input.approverId,
            approverName: input.approverName,
            compOffCalculation: {
              totalHours: accrualResult.calculation.totalHours,
              thresholdHours: accrualResult.calculation.thresholdHours,
              eligibleHours: accrualResult.calculation.eligibleHours,
              weightedHours: accrualResult.calculation.weightedHours,
            },
          },
          createdAt: serverTimestamp(),
          createdBy: input.approverId,
        });
      });

    } catch (err) {
      console.error('대체휴무 적립 실패:', err);
      // 에러가 발생해도 연장근로 승인은 완료된 상태로 유지
    }
  };

  // TODO (Phase 4): 대체휴무 적립 로직을 Cloud Functions에 통합
  // - 연장근로 승인 시 자동 적립
  // - 승인 취소 시 롤백 처리
  // - 잔액 업데이트 및 이력 생성

  /**
   * V155: 승인 요청 취소 (soft delete → status='canceled')
   *
   * 요청자 본인이 pending 상태의 요청을 취소합니다.
   * Cloud Function 호출로 서버에서 권한 및 상태 검증 후 처리합니다.
   */
  const cancelRequest = async (approvalRequestId: string) => {
    if (!user?.profile?.id) {
      throw new Error('로그인이 필요합니다.');
    }

    try {
      setIsProcessing(true);
      setError(null);

      // Cloud Function 호출
      const functions = getFunctions();
      const cancelApprovalRequest = httpsCallable(functions, 'cancelApprovalRequest');

      await cancelApprovalRequest({ approvalRequestId });
    } catch (err: any) {
      console.error('취소 처리 실패:', err);

      // Firebase Functions 에러 처리
      if (err.code) {
        const errorMessage = err.message || '취소 처리 중 오류가 발생했습니다.';
        setError(new Error(errorMessage));
        throw new Error(errorMessage);
      }

      setError(err as Error);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * V156: 승인 요청 삭제 (hard delete)
   *
   * 요청자 본인이 pending 상태의 요청을 완전히 삭제합니다.
   * approvalRequests 문서와 targetDocument 모두 삭제됩니다.
   * Cloud Function 호출로 트랜잭션 처리합니다.
   */
  const deleteRequest = async (approvalRequestId: string) => {
    if (!user?.profile?.id) {
      throw new Error('로그인이 필요합니다.');
    }

    try {
      setIsProcessing(true);
      setError(null);

      // Cloud Function 호출
      const functions = getFunctions();
      const deleteMyApprovalRequest = httpsCallable<
        { approvalRequestId: string },
        { success: boolean; deletedTargetDocument: boolean }
      >(functions, 'deleteMyApprovalRequest');

      const result = await deleteMyApprovalRequest({ approvalRequestId });
      return result.data;
    } catch (err: any) {
      console.error('삭제 처리 실패:', err);

      // Firebase Functions 에러 처리
      if (err.code) {
        const errorMessage = err.message || '삭제 처리 중 오류가 발생했습니다.';
        setError(new Error(errorMessage));
        throw new Error(errorMessage);
      }

      setError(err as Error);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * V50: HR 2단계 승인 처리
   *
   * orgAndHr 플로우에서 조직장 승인 후 HR이 최종 승인합니다.
   * status === 'orgApproved' 상태의 요청만 처리 가능합니다.
   */
  const hrApproveRequest = async (input: ApprovalActionInput) => {
    if (!user?.profile?.id) {
      throw new Error('로그인이 필요합니다.');
    }

    try {
      setIsProcessing(true);
      setError(null);

      // Cloud Function 호출 (V50)
      const functions = getFunctions();
      const hrApproveApprovalRequest = httpsCallable(functions, 'hrApproveApprovalRequest');

      await hrApproveApprovalRequest({
        approvalRequestId: input.approvalRequestId,
        action: input.action,
        comment: input.comment,
      });

    } catch (err: any) {
      console.error('[V50] HR 승인 처리 실패:', err);

      // Firebase Functions 에러 처리
      if (err.code) {
        const errorMessage = err.message || '인사팀 승인 처리 중 오류가 발생했습니다.';
        setError(new Error(errorMessage));
        throw new Error(errorMessage);
      }

      setError(err as Error);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * V55: 승인된 요청의 취소 승인요청 생성
   *
   * 이미 approved 상태인 요청을 취소하기 위한 별도의 승인요청을 생성합니다.
   * - 취소 승인요청이 승인되면 원본 요청이 canceled로 변경됩니다.
   * - 지원 타입: leave, attendanceManual, overtime, weekendWork, holidayWork
   */
  const requestCancelApproval = async (input: {
    originalApprovalRequestId: string;
    reason?: string;
  }) => {
    if (!user?.profile?.id) {
      throw new Error('로그인이 필요합니다.');
    }

    try {
      setIsProcessing(true);
      setError(null);

      const functions = getFunctions();
      const requestCancelApprovalFn = httpsCallable<
        { originalApprovalRequestId: string; reason?: string },
        {
          success: boolean;
          cancelApprovalRequestId: string;
          message: string;
          approvalFlow: 'org' | 'orgAndHr';
          alreadyExists?: boolean;
        }
      >(functions, 'requestCancelApproval');

      const result = await requestCancelApprovalFn(input);
      return result.data;
    } catch (err: any) {
      console.error('[V55] 취소 승인요청 생성 실패:', err);

      // Firebase Functions 에러 처리
      if (err.code) {
        const errorMessage = err.message || '취소 승인요청 생성 중 오류가 발생했습니다.';
        setError(new Error(errorMessage));
        throw new Error(errorMessage);
      }

      setError(err as Error);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * V50: HR 승인 대기 목록 조회
   * user 로딩 전에는 빈 배열 반환 (에러 방지)
   */
  const getHrPendingList = async (companyCode?: string) => {
    // V50: user 없으면 빈 배열 반환 (페이지 진입 시 에러 방지)
    if (!user?.profile?.id) {
      return [];
    }

    try {
      const functions = getFunctions();
      const getHrPendingApprovalsList = httpsCallable<
        { companyCode?: string },
        { success: boolean; approvals: any[]; count: number }
      >(functions, 'getHrPendingApprovalsList');

      const result = await getHrPendingApprovalsList({ companyCode });
      return result.data.approvals;
    } catch (err: any) {
      console.error('[V50] HR 승인 대기 목록 조회 실패:', err);
      throw err;
    }
  };

  /**
   * V55.1: 휴가 취소 직접 승인/반려 (leaveRequest 기반)
   *
   * approvalRequest(B) 없이 leaveRequest(A)를 직접 승인/반려합니다.
   * cancelPending/cancelOrgApproved 상태의 leaveRequest에 사용됩니다.
   *
   * @param leaveRequestId 휴가 신청 ID
   * @param isApprove 승인 여부 (true=승인, false=반려)
   * @param comment 코멘트
   */
  const approveCancelLeaveDirectRequest = async (
    leaveRequestId: string,
    isApprove: boolean,
    comment?: string
  ) => {
    if (!user?.profile?.id) {
      throw new Error('로그인이 필요합니다.');
    }

    try {
      setIsProcessing(true);
      setError(null);

      const functions = getFunctions();
      const approveCancelLeaveRequestFn = httpsCallable<
        { leaveRequestId: string; isApprove: boolean; comment?: string },
        { success: boolean; status: string; message: string; restoredDays?: number }
      >(functions, 'approveCancelLeaveRequest');

      const result = await approveCancelLeaveRequestFn({
        leaveRequestId,
        isApprove,
        comment,
      });

      return result.data;
    } catch (err: any) {
      console.error('[V55.1] 휴가 취소 처리 실패:', err);

      if (err.code) {
        const errorMessage = err.message || '휴가 취소 처리 중 오류가 발생했습니다.';
        setError(new Error(errorMessage));
        throw new Error(errorMessage);
      }

      setError(err as Error);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * V73: HR 휴가 승인 히스토리 조회
   */
  const getHrApprovalHistory = async (year?: number) => {
    if (!user?.profile?.id) {
      return [];
    }

    try {
      const functions = getFunctions();
      const getHrApprovalHistoryListFn = httpsCallable<
        { year?: number },
        { success: boolean; approvals: any[]; count: number }
      >(functions, 'getHrApprovalHistoryList');

      const result = await getHrApprovalHistoryListFn({ year });
      return result.data.approvals;
    } catch (err: any) {
      console.error('[V73] HR 승인 히스토리 조회 실패:', err);
      throw err;
    }
  };

  return {
    createApprovalRequest,
    approveRequest,
    cancelRequest,
    deleteRequest, // V156 추가
    hrApproveRequest, // V50 추가
    getHrPendingList, // V50 추가
    getHrApprovalHistory, // V73 추가: HR 휴가 승인 히스토리
    requestCancelApproval, // V55 추가: 승인된 요청의 취소 승인요청 생성
    approveCancelLeaveDirectRequest, // V55.1: 휴가 취소 직접 승인/반려
    isProcessing,
    error,
  };
}

/**
 * ==============================================
 * Phase 3: Cloud Functions 기반 승인 시스템 완료
 * ==============================================
 *
 * **변경 사항:**
 * 1. approveRequest: Firestore 직접 쓰기 → Cloud Functions 호출
 * 2. processApprovedRequest: Cloud Functions로 이동 (DEPRECATED)
 * 3. accrueCompensatoryLeave: Cloud Functions로 이동 예정 (DEPRECATED)
 *
 * **Self-Approval 방지 (V159 변경):**
 * - 프론트엔드: 사전검증 제거 (버그로 인해 타인 승인까지 차단되는 문제)
 * - Cloud Functions: validateSelfApprovalWithOrgCheck() - 유일한 검증 포인트
 *   - 1인 조직 예외 처리 포함
 *   - requesterId === auth.uid 정확한 비교
 *
 * **다음 단계 (Phase 4):**
 * - 대체휴무 적립 로직을 Cloud Functions에 통합
 * - 휴가 잔액 차감 로직 추가
 * - 연장근로 승인 취소 시 롤백 처리
 */
